import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { PackagingOption } from "~/domains/product/types/PackagingOption";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { Stack, Typography, Collapse, Box } from "~/common/mui";
import { useIsPackagePhasedOut } from "~/features/productDetails/components/PhaseInOut/usePhaseInOutLogic";
import { DiscontinuationNotification } from "~/features/productDetails/components/PhaseInOut/DiscontinuationNotification/DiscontinuationNotification";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import ContactSupport from "~/common/mui/icons/ContactSupport";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";
import StockIndicator from "~/domains/product/components/StockIndicator/StockIndicator";

import { useInStockCalculations, InStockSection } from "./sections/InStockSection";
import { InProductionSection } from "./sections/InProductionSection";
import { MiscSection } from "./sections/MiscSection";
import { PredecessorProductItem } from "./sections/PredecessorProductItem";
import { Header } from "./shared/CollapsibleSection";
import { Root, PerPackStack, Title, SaleTagAbsolute, CheckedIndicatorButton } from "./shared/styled";
import { PreferredPackaging } from "./shared/PreferredPackaging";

// ── Helpers ────────────────────────────────────────────────────────────────

const getPackagingLabel = (pack: PackagingOption, t: TFunction) => {
  if (pack.label === "dts") {
    return t("product.dts");
  }
  if (pack.label === "standard") {
    return t("cartPage.standardPackaging");
  }
  return t("addToCartPopup.packSize", {
    amount: NumberFormatter.getDefaultFormatter().format(pack.packSize),
    code: pack.packCode,
  });
};

// ── Props ──────────────────────────────────────────────────────────────────

interface Props {
  className?: string;
  pack: PackagingOption;
  selected?: boolean;
  onSelect?: () => void;
  initiallyExpanded?: false | "item" | "full";
  disabled?: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

export const PackagingSelectionItem = ({ selected, initiallyExpanded = "full", onSelect, pack, disabled }: Props) => {
  const { t } = useTranslation();

  const [itemExpanded, setItemExpanded] = useState(() => initiallyExpanded !== false);
  const [{ stockExpanded, productionExpanded, miscExpanded }, setState] = useState(() => ({
    stockExpanded: initiallyExpanded === "full",
    productionExpanded: initiallyExpanded === "full",
    miscExpanded: initiallyExpanded === "full",
  }));

  const toggleItem = () => setItemExpanded((prev) => !prev);
  const makeToggle = (key: "stockExpanded" | "productionExpanded" | "miscExpanded") => () =>
    setState((s) => ({ ...s, [key]: !s[key] }));

  const packagingLabel = getPackagingLabel(pack, t);
  const prefQuantity = pack.preferredPackQuantity;
  const saleForPack = pack.stockLevels.inStock.find((entry) => entry.sale);
  const showDiscontinuedWarning = useIsPackagePhasedOut(pack.productDescriptor, pack.packCode);
  const stockCalculations = useInStockCalculations(pack);

  // ── Header ───────────────────────────────────────────────────────────────

  const header = (
    <Header expanded={itemExpanded} onToggle={toggleItem} showToggle={true}>
      <PerPackStack direction={"row"} gap={1}>
        <CheckedIndicatorButton onClick={onSelect} disabled={disabled}>
          <CheckedCircle checked={selected} alwaysVisible />
        </CheckedIndicatorButton>
        <Title sx={{ fontWeight: "bold" }}>{pack.customLabel || packagingLabel}</Title>
        {pack.showContactSupport && (
          <TooltipWithArrow placement="top" title={t("product.phaseOut.info")}>
            <ContactSupport color="textSecondary" sx={{ cursor: "pointer" }} />
          </TooltipWithArrow>
        )}
        {pack.predecessors && pack.predecessors.length > 0 && (
          <Typography
            color="text.label"
            sx={(theme) => ({
              [theme.breakpoints.down("tablet")]: {
                width: "100%",
              },
            })}
          >
            {t("product.phaseOut.fromPredecessor")}
          </Typography>
        )}
        {!itemExpanded && <StockIndicator.V2 stock={stockCalculations.totalInStock} />}
      </PerPackStack>

      {saleForPack && <SaleTagAbsolute size={20} variant={saleForPack.soldOut ? "inactive" : "active"} />}
    </Header>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Root active={selected}>
      {showDiscontinuedWarning && (
        <DiscontinuationNotification discontinued={pack.productDescriptor} variant={"package"} />
      )}

      <Box>
        {header}
        <PreferredPackaging quantity={prefQuantity} sx={{ mt: 0.5 }} />

        <Collapse in={itemExpanded}>
          <Stack gap={2} sx={{ mt: 2 }}>
            {pack.predecessors && pack.predecessors.length > 0 && (
              <Stack gap={2}>
                {pack.predecessors.map((predec) => (
                  <PredecessorProductItem key={predec.sapMaterialNo} product={predec} />
                ))}
              </Stack>
            )}

            <InStockSection
              expanded={stockExpanded}
              onToggle={makeToggle("stockExpanded")}
              pack={pack}
              showToggle={true}
              {...stockCalculations}
            />
            <InProductionSection
              expanded={productionExpanded}
              onToggle={makeToggle("productionExpanded")}
              pack={pack}
              showToggle={true}
            />
            <MiscSection expanded={miscExpanded} onToggle={makeToggle("miscExpanded")} pack={pack} showToggle={true} />
          </Stack>
        </Collapse>
      </Box>
    </Root>
  );
};
